<template>
  <div class="role-create">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="title-section">
          <h1 class="page-title">
            <i class="bi bi-plus-circle me-2"></i>
            Crear Rol
          </h1>
          <p class="page-description">
            Complete el formulario para crear un nuevo rol
          </p>
        </div>
        <div class="actions">
          <router-link to="/roles" class="btn btn-outline-secondary">
            <i class="bi bi-arrow-left me-2"></i>
            Volver
          </router-link>
        </div>
      </div>
    </div>

    <!-- Form Card -->
    <div class="card">
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <!-- Basic Information -->
          <div class="form-section mb-4">
            <h5 class="form-section-title">
              <i class="bi bi-info-circle me-2"></i>
              Información Básica
            </h5>

            <div class="row g-3">
              <div class="col-md-6">
                <label for="name" class="form-label">
                  Nombre del Rol <span class="text-danger">*</span>
                </label>
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.name }"
                  id="name"
                  v-model="formData.name"
                  placeholder="Ej: Administrator"
                  required
                >
                <div class="invalid-feedback" v-if="errors.name">
                  {{ errors.name }}
                </div>
                <small class="form-text text-muted">
                  Nombre único que identifica el rol (1-100 caracteres)
                </small>
              </div>
            </div>
          </div>

          <!-- Privileges Section -->
          <div class="form-section mb-4">
            <h5 class="form-section-title">
              <i class="bi bi-shield-check me-2"></i>
              Privilegios
            </h5>

            <div class="alert alert-info">
              <i class="bi bi-info-circle me-2"></i>
              Configure los privilegios para cada módulo del sistema. Puede agregar módulos personalizados.
            </div>

            <!-- Module Tabs -->
            <ul class="nav nav-tabs mb-3" id="privilegesTabs" role="tablist">
              <li class="nav-item" role="presentation" v-for="(module, index) in privilegeModules" :key="module.key">
                <button
                  class="nav-link"
                  :class="{ active: index === 0 }"
                  :id="`tab-${module.key}`"
                  data-bs-toggle="tab"
                  :data-bs-target="`#content-${module.key}`"
                  type="button"
                  role="tab"
                >
                  <i :class="module.icon" class="me-2"></i>
                  {{ module.label }}
                </button>
              </li>
            </ul>
            <!-- Module Contents -->
              <div class="tab-content" id="privilegesTabContent">
                <div
                  v-for="(module, index) in privilegeModules"
                  :key="module.key"
                  class="tab-pane fade"
                  :class="{ 'show active': index === 0 }"
                  :id="`content-${module.key}`"
                  role="tabpanel"
                >
                  <div class="row g-2">
                    <div class="col-md-3" v-for="permission in module.permissions" :key="permission.value">
                      <div class="form-check">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          :id="`${module.key}-${permission.value}`"
                          :value="permission.value"
                          v-model="formData.privileges[module.key]"
                        >
                        <label class="form-check-label" :for="`${module.key}-${permission.value}`">
                          <i :class="permission.icon" class="me-1"></i>
                          {{ permission.label }}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div class="mt-3">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary me-2"
                      @click="selectAllPermissions(module.key)"
                    >
                      <i class="bi bi-check-all me-1"></i>
                      Seleccionar Todos
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      @click="clearAllPermissions(module.key)"
                    >
                      <i class="bi bi-x-circle me-1"></i>
                      Limpiar
                    </button>
                  </div>
                </div>
              </div>
              <!-- Preview Section -->
            <div class="form-section mb-4" v-if="Object.keys(formData.privileges).some(key => formData.privileges[key]?.length > 0)">
              <h5 class="form-section-title">
                <i class="bi bi-eye me-2"></i>
                Vista Previa de Privilegios
              </h5>
              <div class="privilege-preview">
                <div v-for="(permissions, module) in formData.privileges" :key="module">
                  <div v-if="permissions && permissions.length > 0" class="mb-2">
                    <strong>{{ getModuleLabel(module) }}:</strong>
                    <span v-for="permission in permissions" :key="permission" class="badge bg-primary ms-1">
                      {{ permission }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>


          <!-- Form Actions -->
          <div class="form-actions">
            <router-link to="/roles" class="btn btn-secondary">
              <i class="bi bi-x-lg me-2"></i>
              Cancelar
            </router-link>
            <button type="submit" class="btn btn-primary" :disabled="roleStore.loading">
              <span v-if="roleStore.loading">
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Creando...
              </span>
              <span v-else>
                <i class="bi bi-check-lg me-2"></i>
                Crear Rol
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useRoleStore } from '../../stores/roles'

// Router and Store
const router = useRouter()
const roleStore = useRoleStore()

// Form data
const formData = reactive({
  name: '',
  privileges: {
    clients: [],
    scripts: [],
    roles: [],
    users: [],
    system: []
  }
})

const errors = ref({})

// Privilege modules configuration
const privilegeModules = ref([
  {
    key: 'clients',
    label: 'Clientes',
    icon: 'bi bi-building',
    permissions: [
      { value: 'create', label: 'Crear', icon: 'bi bi-plus-circle' },
      { value: 'read', label: 'Ver', icon: 'bi bi-eye' },
      { value: 'update', label: 'Editar', icon: 'bi bi-pencil' },
      { value: 'delete', label: 'Eliminar', icon: 'bi bi-trash' }
    ]
  },
  {
    key: 'scripts',
    label: 'Scripts',
    icon: 'bi bi-code-square',
    permissions: [
      { value: 'execute', label: 'Ejecutar', icon: 'bi bi-play-circle' },
      { value: 'stop', label: 'Detener', icon: 'bi bi-stop-circle' },
      { value: 'view_logs', label: 'Ver Logs', icon: 'bi bi-file-text' },
      { value: 'delete', label: 'Eliminar', icon: 'bi bi-trash' }
    ]
  },
  {
    key: 'roles',
    label: 'Roles',
    icon: 'bi bi-shield-lock',
    permissions: [
      { value: 'create', label: 'Crear', icon: 'bi bi-plus-circle' },
      { value: 'read', label: 'Ver', icon: 'bi bi-eye' },
      { value: 'update', label: 'Editar', icon: 'bi bi-pencil' },
      { value: 'delete', label: 'Eliminar', icon: 'bi bi-trash' }
    ]
  },
  {
    key: 'users',
    label: 'Usuarios',
    icon: 'bi bi-people',
    permissions: [
      { value: 'create', label: 'Crear', icon: 'bi bi-plus-circle' },
      { value: 'read', label: 'Ver', icon: 'bi bi-eye' },
      { value: 'update', label: 'Editar', icon: 'bi bi-pencil' },
      { value: 'delete', label: 'Eliminar', icon: 'bi bi-trash' },
      { value: 'manage_tokens', label: 'Gestionar Tokens', icon: 'bi bi-key' }
    ]
  },
  {
    key: 'system',
    label: 'Sistema',
    icon: 'bi bi-gear',
    permissions: [
      { value: 'manage_settings', label: 'Configuración', icon: 'bi bi-sliders' },
      { value: 'view_logs', label: 'Ver Logs', icon: 'bi bi-file-text' },
      { value: 'manage_users', label: 'Gestionar Usuarios', icon: 'bi bi-people' }
    ]
  }
])


// Methods
const selectAllPermissions = (moduleKey) => {
  const module = privilegeModules.value.find(m => m.key === moduleKey)
  if (module) {
    formData.privileges[moduleKey] = module.permissions.map(p => p.value)
  }
}

const clearAllPermissions = (moduleKey) => {
  formData.privileges[moduleKey] = []
}

const getModuleLabel = (moduleKey) => {
  const module = privilegeModules.value.find(m => m.key === moduleKey)
  return module ? module.label : moduleKey
}


const validateForm = () => {
  errors.value = {}

  if (!formData.name || formData.name.trim().length === 0) {
    errors.value.name = 'El nombre del rol es requerido'
  } else if (formData.name.length > 100) {
    errors.value.name = 'El nombre no puede exceder 100 caracteres'
  }

  return Object.keys(errors.value).length === 0
}

const handleSubmit = async () => {
  if (!validateForm()) {
    return
  }

  try {
    // Filter out empty privilege arrays
    const filteredPrivileges = {}
    Object.keys(formData.privileges).forEach(key => {
      if (formData.privileges[key].length > 0) {
        filteredPrivileges[key] = formData.privileges[key]
      }
    })

    const roleData = {
      name: formData.name.trim(),
      privileges: Object.keys(filteredPrivileges).length > 0 ? filteredPrivileges : null
    }

    await roleStore.createRole(roleData)

    // Navigate back to roles list
    router.push('/roles')
  } catch (error) {
    console.error('Error creating role:', error)
    if (error.response?.data?.detail) {
      errors.value.name = error.response.data.detail
    }
  }
}

// Lifecycle
import { onMounted } from 'vue'

onMounted(() => {
})
</script>

<style scoped>
.role-create {
  padding: 0;
}

.page-header {
  background: var(--card-bg);
  border-bottom: 1px solid var(--border-color);
  padding: 1.5rem 0;
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.title-section h1 {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  color: var(--text-primary);
}

.title-section p {
  margin: 0.25rem 0 0;
  color: var(--text-secondary);
}

.card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
}

.form-section {
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--border-color);
}

.form-section:last-of-type {
  border-bottom: none;
}

.form-section-title {
  color: var(--text-primary);
  font-weight: 600;
  margin-bottom: 1rem;
}

.form-label {
  color: var(--text-primary);
  font-weight: 500;
}

.form-control {
  background: var(--input-bg);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.form-control:focus {
  background: var(--input-bg);
  border-color: var(--bs-primary);
  color: var(--text-primary);
}

.form-check-input {
  background-color: var(--input-bg);
  border-color: var(--border-color);
}

.form-check-input:checked {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
}

.form-check-label {
  color: var(--text-primary);
}

.nav-tabs {
  border-color: var(--border-color);
}

.nav-tabs .nav-link {
  color: var(--text-secondary);
  border-color: transparent;
}

.nav-tabs .nav-link:hover {
  border-color: var(--border-color);
  color: var(--text-primary);
}

.nav-tabs .nav-link.active {
  background: var(--card-bg);
  color: var(--bs-primary);
  border-color: var(--border-color) var(--border-color) var(--card-bg);
}

.privilege-preview {
  background: var(--hover-bg);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 1rem;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .form-actions {
    flex-direction: column-reverse;
  }

  .form-actions .btn {
    width: 100%;
  }
}
</style>
